import { useAuth0 } from "@auth0/auth0-react";
import { ArrowUpRightIcon, FolderIcon } from "@heroicons/react/24/outline";
import { ApiStatusToFileStatus } from "@utils/constants";
import { downloadLearnerFileById } from "@utils/helpers";
import React, { useState } from "react";
import { Link } from "react-router-dom";

import { Headline, Paragraph } from "@fronterahealth/frontera-ui-components";

import {
  ApiLearnerFileStatusChoices,
  ApiReportReportTypeChoices,
  ApiReportTemplateFileUploadConfigChoices,
  AssessmentToolType,
  LearnerFileType,
} from "@api/graphql/types-and-hooks";
import { DocumentReviewModal } from "@components/DocumentReviewModal/DocumentReviewModal";
import {
  ExistingFileWithMime,
  FileUploadClassifyAndParse,
} from "@components/UploadDocuments/FileUploadClassifyAndParse";
import { UploadDocumentsGuide } from "@components/UploadDocuments/UploadDocumentsGuide";
import { getDefaultTranscribeLanguage } from "@components/UploadDocuments/transcribeLanguage";
import {
  useGetFileParseStatusRetriever,
  useGetRemoveFileCallback,
  useGetReportFileUploadCompleteCallback,
  useGetS3BucketUploadCallback,
  useGetS3BucketUrlRetriever,
} from "@pages/ClinicalReportDetails/UploadFiles/hooks";
import { useAdminData } from "@providers/AdminDataProvider";

interface UploadDocumentsProps {
  existingFiles: LearnerFileType[];
  supportedAssessments: AssessmentToolType[];
  reportType: ApiReportReportTypeChoices | undefined;
  fileUploadConfig: ApiReportTemplateFileUploadConfigChoices | undefined;
  reportId?: string;
  learnerId?: string;
  learnerName?: string;
}

export const UploadDocuments: React.FC<UploadDocumentsProps> = ({
  existingFiles,
  supportedAssessments,
  reportType,
  fileUploadConfig,
  reportId,
  learnerId,
  learnerName,
}) => {
  const isReassessment = reportType === ApiReportReportTypeChoices.Reassessment;
  const { isKuwaitUser } = useAdminData();

  const S3BucketUrlRetriever = useGetS3BucketUrlRetriever({
    transcribeLanguage: getDefaultTranscribeLanguage(isKuwaitUser),
  });
  const reportFileUploadCompleteCallback = useGetReportFileUploadCompleteCallback({
    mode: isReassessment ? "reassessment-classify" : "classify",
  });
  const S3BucketUploadCallback = useGetS3BucketUploadCallback();
  const fileParseStatusRetriever = useGetFileParseStatusRetriever();
  const removeFileCallback = useGetRemoveFileCallback();
  const { getAccessTokenSilently } = useAuth0();

  const [orderedFileNames, setOrderedFileNames] = useState<string[]>([]);
  const [reviewFileId, setReviewFileId] = useState<string | null>(null);

  const existingFilesMapped: ExistingFileWithMime[] =
    existingFiles?.map((existingFile: LearnerFileType) => ({
      fileName: existingFile.originalFileName || "<missing-file-name>",
      fileSize: existingFile.fileSize,
      fileStatus: ApiStatusToFileStatus[existingFile.status || ApiLearnerFileStatusChoices.Pending],
      metadata: existingFile.metadata,
      mimeType: existingFile.mimeType,
      fileKind: existingFile.fileKind || "<missing-file-kind>",
      fileId: existingFile.id,
      failureReason: existingFile.processingFailureReason || "",
      failureCode: existingFile.processingFailureCode,
    })) || [];

  const title = isReassessment ? "Upload Prior Report & Assessments" : "Upload Documents";

  return (
    <div className="w-full rounded-xl border border-border-strong bg-white p-6">
      <div className="flex flex-col mb-4">
        <Headline size="medium">{title}</Headline>
        <Paragraph size="small" className="text-text-tertiary">
          {isReassessment
            ? "Upload your standardized assessments and the most recent Initial Assessment or Reassessment report."
            : "Upload your files: assessments, records, notes, and more."}{" "}
          Files are automatically categorized; adjust any category as needed. Frontera&apos;s assessment scoring
          templates are available to download{" "}
          <Link to="/downloads" className="text-interactive-primary-default underline">
            here
          </Link>
          .
        </Paragraph>
      </div>

      <div className="mb-4">
        <UploadDocumentsGuide reportType={reportType} fileUploadConfig={fileUploadConfig} />
      </div>

      <FileUploadClassifyAndParse
        maxSize={"2GB"}
        existingFiles={existingFilesMapped}
        supportedAssessments={supportedAssessments}
        S3BucketUrlRetriever={S3BucketUrlRetriever}
        S3BucketUploadCallback={S3BucketUploadCallback}
        reportFileUploadCompleteCallback={reportFileUploadCompleteCallback}
        fileParseStatusRetriever={fileParseStatusRetriever}
        removeFileCallback={removeFileCallback}
        allowFileDownload={true}
        downloadFileCallback={async (fileId) => {
          const token = await getAccessTokenSilently();
          await downloadLearnerFileById({ fileId: fileId, token: token });
        }}
        previewFileCallback={async (fileId) => {
          setReviewFileId(fileId);
        }}
        onOrderChanged={(fileNames) => {
          setOrderedFileNames((prev) =>
            prev.length === fileNames.length && prev.every((name, i) => name === fileNames[i]) ? prev : fileNames,
          );
        }}
        reportType={reportType}
        fileUploadConfig={fileUploadConfig}
        reportId={reportId}
        learnerId={learnerId}
        emptyStateText={
          isReassessment
            ? "Upload assessments and most recent previous Initial Assessment or Reassessment report all at once."
            : undefined
        }
      />
      {learnerId && (
        <Link
          to={`/clients/${learnerId}/file-library`}
          className="ph-no-capture mt-8 inline-flex w-fit items-center gap-1.5 text-sm text-interactive-primary-default underline hover:text-interactive-primary-hover"
        >
          <FolderIcon aria-hidden="true" className="h-4 w-4" />
          {learnerName ? <>View all of {learnerName}&apos;s files</> : "View all files"}
          <ArrowUpRightIcon aria-hidden="true" className="h-3.5 w-3.5" />
        </Link>
      )}
      {reviewFileId && (
        <DocumentReviewModal
          fileId={reviewFileId}
          existingFiles={[...existingFiles].sort(
            (a, b) => orderedFileNames.indexOf(a.originalFileName) - orderedFileNames.indexOf(b.originalFileName),
          )}
          supportedAssessments={supportedAssessments}
          onClose={() => setReviewFileId(null)}
          setFileId={(fileId) => setReviewFileId(fileId)}
        />
      )}
    </div>
  );
};
